import React, { useState } from 'react';
import { Badge, Button, Input, ReadOnlyValue } from '../../ui';
import { trpClient } from '../../../client/trpClient';
import { RFQBuyerGstin } from '../../../types';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';

/**
 * The buyer's own GSTIN on this RFQ (flow D, ADR 0008), in step 2's Logistics
 * card. Shown when the RFQ carries one (copied from the company at creation);
 * otherwise the buyer may type it. Optional: nothing waits for it.
 */
interface BuyerGstinFieldProps {
  rfqId: string;
  initial?: RFQBuyerGstin | null;
  readOnly?: boolean;
  disabled?: boolean;
}

// Same shape the server checks; the server also checks the check character.
const GSTIN_SHAPE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const normalize = (value: string) => value.replace(/\s+/g, '').toUpperCase();

const BuyerGstinField: React.FC<BuyerGstinFieldProps> = ({ rfqId, initial, readOnly, disabled }) => {
  const [saved, setSaved] = useState<RFQBuyerGstin | null>(initial ?? null);
  const [gstin, setGstin] = useState('');
  const [city, setCity] = useState('');
  const [askCity, setAskCity] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  if (saved) {
    return (
      <div className="flex items-center gap-s2">
        <span className="font-mono text-body-md text-ink">{saved.value}</span>
        {saved.status === 'verified' ? (
          <Badge tone="action">Verified</Badge>
        ) : (
          <Badge title="Not yet checked against the GST registry">Unverified</Badge>
        )}
      </div>
    );
  }
  if (readOnly) return <ReadOnlyValue value="" emptyLabel="No GSTIN on this RFQ" />;

  const value = normalize(gstin);
  const save = async () => {
    if (!GSTIN_SHAPE.test(value)) {
      setError('A GSTIN has 15 characters and starts with your state code, like 33AAACR5055K1ZE.');
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      setSaved(await trpClient.setBuyerGstin(rfqId, value, city.trim() || undefined));
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code === 'CITY_REQUIRED') setAskCity(true);
      setError(apiErrorMessage(err, 'I couldn\'t save that GSTIN. Try again in a moment.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-s2">
      <div className="flex items-start gap-s2">
        <div className="flex-1">
          <Input
            aria-label="Your GSTIN"
            value={gstin}
            onChange={(e) => { setGstin(e.target.value); setError(undefined); }}
            placeholder="e.g. 33AAACR5055K1ZE"
            maxLength={20}
            disabled={disabled || saving}
            error={askCity ? undefined : error}
            helper={askCity ? undefined : 'Goes on this RFQ and your purchase documents.'}
          />
        </div>
        <Button variant="secondary" onClick={save} loading={saving} disabled={disabled || !value}>Save</Button>
      </div>
      {askCity && (
        <Input
          aria-label="City for this GSTIN"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="e.g. Chennai"
          maxLength={100}
          disabled={disabled || saving}
          error={error}
        />
      )}
    </div>
  );
};

export default BuyerGstinField;
